import {Dialog, DialogContent, DialogTitle, IconButton, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';
import type {VideosManifestEntry} from '@/types/videosManifest';
import {buildVideoAssetUrl} from './videosMenuUtils';
import {useIsMobile} from '@/hooks/useIsMobile';
import './VideoPlayerDialog.css';

interface VideoPlayerDialogProps {
    selectedVideo: VideosManifestEntry | null;
    onClose: () => void;
}

const VideoPlayerDialog = ({selectedVideo, onClose}: VideoPlayerDialogProps) => {
    const {t} = useTranslation();
    const isMobile = useIsMobile();

    if (!selectedVideo) return null;

    const videoUrl = buildVideoAssetUrl(selectedVideo.file);

    if (isMobile) {
        return (
            <Dialog
                open
                onClose={onClose}
                fullScreen
                className="video-player-dialog video-player-dialog--mobile"
            >
                <div className="video-player-mobile-header">
                    <IconButton
                        onClick={onClose}
                        className="video-player-back-btn"
                        aria-label={t('common.close')}
                        edge="start"
                        size="small"
                    >
                        <CloseIcon fontSize="small"/>
                    </IconButton>
                    <Typography component="h2" className="video-player-mobile-title">
                        {selectedVideo.title}
                    </Typography>
                </div>
                <div className="video-player-mobile-content">
                    <video
                        key={videoUrl}
                        src={videoUrl}
                        className="video-player-element"
                        controls
                        controlsList="nodownload"
                        preload="metadata"
                        playsInline
                    />
                </div>
            </Dialog>
        );
    }

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth={false}
            className="video-player-dialog"
            slotProps={{
                paper: {
                    sx: {
                        width: 'min(1600px, calc(100vw - 32px))',
                        height: '90vh',
                        maxWidth: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                },
            }}
        >
            <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2}}>
                <Typography component="span" variant="h6" sx={{flex: 1}}>
                    {selectedVideo.title}
                </Typography>
                <IconButton onClick={onClose} aria-label={t('common.close')}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{p: 0, background: '#000', flex: 1, display: 'flex'}}>
                <video
                    key={videoUrl}
                    src={videoUrl}
                    className="video-player-element"
                    controls
                    controlsList="nodownload"
                    preload="metadata"
                />
            </DialogContent>
        </Dialog>
    );
};

export default VideoPlayerDialog;
